import { ApiError } from '@gorila-shared-ui/components';
import { useMap } from 'react-map-gl/mapbox';
import { useParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { SPOT_TYPE_IDS } from '../../constants/spots';
import useEffectOnce from '../../hooks/useEffectOnce';
import useUpdateEffect from '../../hooks/useUpdateEffect';
import { getPublicSpotsFileKey, getSpotsFile, getSpotsFileKey } from '../../services/spotsService';
import { isPublicPageState } from '../../storage/app';
import { spotsGeojsonState } from '../../storage/map';

export function SpotsProvider() {
  const { map } = useMap();
  const setSpotsGeojson = useSetRecoilState(spotsGeojsonState);
  const isPublicPage = useRecoilValue(isPublicPageState);
  const { shareLinkId } = useParams();

  useEffectOnce(() => {
    const handleFileRead = (event: any, spotTypeId: string) => {
      const content = JSON.parse(event.target.result);
      setSpotsGeojson((prev) => (prev ? { ...prev, [spotTypeId]: content } : { [spotTypeId]: content }));
    };

    const getPublicSpotsFile = async (url: string) => {
      const response = await fetch(url);
      if (response.status === 200 && response.body) {
        return {
          file: await new Response(response.body).blob(),
          error: undefined,
        };
      }
      return {
        file: undefined,
        error: '',
      };
    };

    const handleResponse = async ({
      url,
      key,
      spotTypeId,
      error,
    }: {
      key?: string;
      url?: string;
      spotTypeId: string;
      error?: ApiError;
    }) => {
      if (!error && (key || url)) {
        const { file, error: fileError } = await (url ? getPublicSpotsFile(url) : getSpotsFile(key!));
        if (!fileError && file) {
          const reader = new FileReader();
          reader.onload = (event: any) => {
            handleFileRead(event, spotTypeId);
          };
          reader.readAsText(file);
        } else {
          setSpotsGeojson(undefined);
        }
      } else {
        setSpotsGeojson(undefined);
      }
    };

    const loadSpotsFile = async (spotTypeId: string) => {
      if (isPublicPage && shareLinkId) {
        const { url, error } = await getPublicSpotsFileKey(shareLinkId, spotTypeId);
        await handleResponse({ url, spotTypeId, error });
      } else {
        const { key, error } = await getSpotsFileKey(spotTypeId);
        await handleResponse({ key, spotTypeId, error });
      }
    };

    SPOT_TYPE_IDS.forEach((spotTypeId) => loadSpotsFile(spotTypeId));
  });

  useUpdateEffect(() => {
    if (!map) return;
    const mapIconUrls = [
      '/img/icons/map/gas_stations.png',
      '/img/icons/map/hotels.png',
      '/img/icons/map/junkyard.png',
      '/img/icons/map/workshops.png',
    ];
    mapIconUrls.forEach(async (iconUrl) => {
      map.loadImage(`${iconUrl}`, (error, image) => {
        const iconName = iconUrl.substring(5, iconUrl.length);
        if (error) {
          return;
        }
        if (image && !map.hasImage(iconName)) {
          map.addImage(iconName, image);
        }
      });
    });
  }, [map]);

  return null;
}
